import React, { useState, useEffect } from 'react';
import { brandingAPI } from '../services/api';
import pamhLogo from '../assets/pamh-logo.png';
import { logoOnDark } from '../utils/logoOnDark';

// ==================== BRANDING (custom uploaded logo) ====================
// A logo uploaded under Settings → Branding replaces the built-in one everywhere this component
// renders. Fetched once per page load and shared across every instance; refreshBranding() (called
// by the Branding settings page after upload/remove) re-fetches and notifies mounted logos so the
// whole app updates without a reload.
let brandingPromise = null;
const fetchBranding = (force = false) => {
  if (!brandingPromise || force) {
    brandingPromise = brandingAPI.get().catch(() => ({ hasCustomLogo: false }));
  }
  return brandingPromise;
};
export const refreshBranding = () => {
  fetchBranding(true);
  window.dispatchEvent(new Event('clinic:branding-changed'));
};

// The logo's navy, for the "PAMH" wordmark beside it.
const NAVY = '#003078';

/**
 * Logo for Perveen Aslam Memorial Hospital (PAMH): the uploaded logo when there is one, otherwise
 * the PAMH mark bundled with the app (four figures round a mother and child, 2026-10-01).
 *
 * The mark is drawn in navy, green, gold and sky blue, and its navy parts (the PAMH letters and
 * the top figure) vanish on the navy side pane and sign-in panel. On a dark background (variant
 * 'white') it is shown REVERSED, its dark colours turned white (utils/logoOnDark), so it sits
 * straight on whatever is behind it with no tile (owner, 2026-10-02). Only if the image cannot be
 * reversed does it fall back to a white tile, so the letters are never lost.
 *
 * @param {number} size - Width/height of the mark in pixels (default 40)
 * @param {string} className - Additional CSS classes
 * @param {boolean} showText - Show "PAMH / Hospital System" beside the mark (default false)
 * @param {string} variant - 'color' on light backgrounds, 'white' on dark ones (default 'color')
 */
const ClinicLogo = ({ size = 40, className = '', showText = false, variant = 'color', textClassName = '' }) => {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetchBranding().then(b => { if (alive) setBranding(b); });
    load();
    window.addEventListener('clinic:branding-changed', load);
    return () => { alive = false; window.removeEventListener('clinic:branding-changed', load); };
  }, []);

  const onDark = variant === 'white';
  // Until the branding answer arrives the bundled mark shows, so the frame never flashes empty.
  const src = branding?.hasCustomLogo ? brandingAPI.logoUrl(branding.logoVersion) : pamhLogo;
  const tilePad = Math.round(size * 0.12);

  // The reversed logo for a dark background: undefined while it is being made, null if it cannot be.
  const [reversed, setReversed] = useState(undefined);
  useEffect(() => {
    if (!onDark) return undefined;
    let alive = true;
    setReversed(undefined);
    logoOnDark(src).then((url) => { if (alive) setReversed(url); });
    return () => { alive = false; };
  }, [onDark, src]);
  const useTile = onDark && reversed === null;

  const mark = (
    <img
      src={onDark && reversed ? reversed : src}
      alt="Perveen Aslam Memorial Hospital logo"
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        // Hidden for the moment it takes to reverse, so the navy letters never flash invisible.
        visibility: onDark && reversed === undefined ? 'hidden' : 'visible',
      }}
    />
  );

  return (
    <div className={`inline-flex items-center ${className}`} style={{ gap: size * 0.3 }}>
      {useTile ? (
        <span
          style={{
            display: 'inline-flex',
            padding: tilePad,
            background: '#ffffff',
            borderRadius: Math.round(size * 0.3),
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        >
          {mark}
        </span>
      ) : (
        mark
      )}
      {showText && (
        <div style={{ lineHeight: 1.1 }}>
          <div
            className={textClassName}
            style={{
              fontWeight: 700,
              fontSize: size * 0.42,
              color: onDark ? '#ffffff' : NAVY,
              letterSpacing: '0.05em',
            }}
          >
            PAMH
          </div>
          <div
            style={{
              fontSize: size * 0.2,
              color: onDark ? 'rgba(255,255,255,0.75)' : '#6b7280',
              fontWeight: 500,
            }}
          >
            Hospital System
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicLogo;
